import { useMutation, useQuery } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateCustomerContactRequest,
  CustomerContact,
} from './CustomerContacts.types';

import {
  listCustomerContacts,
  deleteCustomerContact,
  removeCustomerContactDetail,
  createCustomerContact,
  updateCustomerContact,
} from './CustomerContacts.service';
import { useIntl } from 'react-intl';
import { notification } from 'antd';
import Authorities from '@/types/Authority';
import { queryClient } from '@/Root';
import notificationError from '@/utils/functions/notificationError';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useCustomerContacts(customerId?: number, enabled = true) {
  const cache = queryClient.getQueryData(['customer/contacts', customerId]);

  return useQuery(
    ['customer/contacts', customerId],
    () =>
      !!customerId
        ? listCustomerContacts(customerId).then((response) => response.data)
        : [],
    {
      enabled:
        hasPermissions([Authorities.REGISTER_CUSTOMER_VIEW]) &&
        enabled &&
        !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCustomerContactsMutation() {
  return useMutation(
    async (customerId: number) => {
      const cache = queryClient.getQueryData<CustomerContact[]>([
        'customer/contacts',
        customerId,
      ]);

      if (!!cache) return cache;

      return listCustomerContacts(customerId).then((response) => response.data);
    },
    {
      onSuccess(data, vars) {
        queryClient.setQueryData(['customer/contacts', vars], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCreateCustomerContact() {
  const intl = useIntl().formatMessage;
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (data: { value: CreateCustomerContactRequest; customerId: number }) =>
      createCustomerContact({ value: data.value, customerId: data.customerId }),
    {
      async onSuccess(_, vars) {
        notification.success({
          message: intl({ id: 'commons.modal.contact.added.message' }),
          description: intl({
            id: 'commons.modal.contact.added.description',
          }),
        });

        queryChanel({ resetQueries: [['customer/contacts', vars.customerId]] });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useCustomerContactDetailRemove() {
  const intl = useIntl().formatMessage;
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (data: { customerId: number; detailId: number }) =>
      removeCustomerContactDetail(data.customerId, data.detailId),
    {
      onSuccess(_, vars) {
        notification.success({
          message: intl({ id: 'commons.modal.contact.removed.message' }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });

        queryChanel({ resetQueries: [['customer/contacts', vars.customerId]] });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateCustomerContact() {
  const intl = useIntl().formatMessage;
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (
      data: CreateCustomerContactRequest & {
        customerId: number;
        id: number;
      },
    ) => updateCustomerContact(data),
    {
      onSuccess(_, vars) {
        notification.success({
          message: intl({ id: 'commons.modal.contact.updated.message' }),
          description: intl({
            id: 'commons.modal.contact.updated.description',
          }),
        });

        queryChanel({ resetQueries: [['customer/contacts', vars.customerId]] });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
export function useDeleteCustomerContact() {
  const intl = useIntl().formatMessage;
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (data: { customerId: number; contactId: number }) =>
      deleteCustomerContact(data.customerId, data.contactId),
    {
      async onSuccess(_, vars) {
        notification.success({
          message: intl({ id: 'commons.modal.contact.removed.message' }),
          description: intl({
            id: 'commons.modal.contact.removed.description',
          }),
        });

        queryChanel({ resetQueries: [['customer/contacts', vars.customerId]] });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
